import { useAtsConversationApi, useAtsProjectsApi } from '@/composables/useApi';

import {
  type HasOpenThreadResponseDto,
  type InterviewSchedulingRequestDto,
  InterviewType,
} from './types/interview-scheduling.type';
import type { InterviewSchedulingEvent } from './types/interview-scheduling-event.type';
import ProProfileService from '@/core/conversations/pro-profile/pro-profile.service';
import MeService from '../me/me.service';
import { useInterviewSchedulingStore } from './interview-scheduling.store';
import type { ProjectInterviewSettings } from '@/core/jobs/copilot-activation/types/interview-scheduling-settings.type';
import CalendarService from '@/core/shared/calendar/calendar.service';
import { InterviewSchedulingPersistence } from './interview-scheduling.persistence';
import { ErrorService } from '../errors/error.service';

export interface UpcomingInterview {
  scheduleDateTs: string;
  interviewTypeDisplay: string;
  jobTitle: string;
}

export interface CustomMeetingLinkValidationOptions {
  needsUpgrade: boolean;
  isVideoInterview: boolean;
  customMeetingLink?: string;
}

export default class InterviewSchedulingService {
  interviewSchedulingStore = useInterviewSchedulingStore();
  interviewSchedulingPersistence = new InterviewSchedulingPersistence();

  proProfileService = new ProProfileService();
  meService = new MeService();
  calendarService = new CalendarService();

  /**
   * Maps InterviewType enum to display text for the banner
   */
  private getInterviewTypeDisplay(type: InterviewType): string {
    const typeMap: Record<InterviewType, string> = {
      [InterviewType.video]: 'Video interview',
      [InterviewType.phone]: 'Phone interview',
      [InterviewType.on_site]: 'In-person interview',
    };
    return typeMap[type] || 'Interview';
  }

  /**
   * Fetches interview scheduling events for a conversation.
   * Deduplicates: if already fetching for the same conversation, returns the in-flight promise.
   */
  fetchScheduledEvents(employerId: number, conversationId: number): Promise<void> {
    const store = this.interviewSchedulingStore;

    // Dedup: if already fetching for this conversation, return existing promise
    if (
      store.scheduledEventsConversationId === conversationId &&
      store.scheduledEventsFetchPromise
    ) {
      return store.scheduledEventsFetchPromise;
    }

    store.scheduledEventsConversationId = conversationId;
    store.scheduledEvents = [];

    const promise = this.performFetchScheduledEvents(employerId, conversationId);
    store.scheduledEventsFetchPromise = promise;

    return promise;
  }

  private async performFetchScheduledEvents(
    employerId: number,
    conversationId: number,
  ): Promise<void> {
    const store = this.interviewSchedulingStore;

    try {
      const events = await this.interviewSchedulingPersistence.getEventsByConversationId(
        employerId,
        conversationId,
      );
      if (store.scheduledEventsConversationId === conversationId) {
        store.scheduledEvents = events;
      }
    } catch (error) {
      if (store.scheduledEventsConversationId === conversationId) {
        store.scheduledEvents = [];
        store.scheduledEventsConversationId = null;
      }
      ErrorService.captureException(error);
    }
  }

  async fetchRecentLocations(): Promise<void> {
    const employerId = this.meService.employer?.id;
    if (!employerId) {
      return;
    }

    try {
      this.interviewSchedulingStore.recentLocations =
        await this.interviewSchedulingPersistence.getRecentLocations(employerId);
    } catch (error) {
      this.interviewSchedulingStore.recentLocations = [];
      ErrorService.captureException(error);
    }
  }

  get recentLocations(): string[] {
    return this.interviewSchedulingStore.recentLocations;
  }

  /**
   * Clears scheduled events when conversation changes
   */
  clearScheduledEvents(): void {
    this.interviewSchedulingStore.scheduledEvents = [];
    this.interviewSchedulingStore.scheduledEventsConversationId = null;
    this.interviewSchedulingStore.scheduledEventsFetchPromise = null;
  }

  /**
   * Returns the next upcoming scheduled interview (closest future date)
   * Returns null if no upcoming interviews exist
   */
  get nextUpcomingInterview(): UpcomingInterview | null {
    const events = this.interviewSchedulingStore.scheduledEvents;
    const now = new Date();

    let closestEvent: InterviewSchedulingEvent | null = null;
    let closestDate: Date | null = null;

    for (const event of events) {
      if (!event.scheduleDateTs) {
        continue;
      }
      const eventDate = new Date(event.scheduleDateTs);

      if (eventDate > now) {
        if (!closestDate || eventDate < closestDate) {
          closestEvent = event;
          closestDate = eventDate;
        }
      }
    }

    if (!closestEvent) {
      return null;
    }

    return {
      scheduleDateTs: closestEvent.scheduleDateTs as string,
      interviewTypeDisplay: this.getInterviewTypeDisplay(closestEvent.type),
      jobTitle: closestEvent.jobTitle || '',
    };
  }

  /**
   * Returns true if there is an upcoming scheduled interview
   */
  get hasUpcomingInterview(): boolean {
    const now = new Date();
    return this.interviewSchedulingStore.scheduledEvents.some(
      (event) => event.scheduleDateTs && new Date(event.scheduleDateTs) > now,
    );
  }

  public async sendInterviewSchedulingRequest(
    jobApplicantId: number,
    payload: InterviewSchedulingRequestDto,
  ): Promise<void> {
    const employerId = this.meService.employer?.id;
    if (!employerId) {
      throw new Error('Employer ID is required');
    }
    const { error } = await useAtsConversationApi(
      `/interview-scheduling/job-applicant/${jobApplicantId}/initiate-interview`,
    ).post(payload);

    if (error.value) {
      throw error.value;
    }
  }

  async updateOngoingInterviewThreadCheck(): Promise<void> {
    const userProfileId = this.proProfileService.selectedProApplication?.userProfileId;
    if (!userProfileId) {
      return;
    }
    const employerId = this.meService.employer?.id;

    const { error, data } = await useAtsConversationApi<HasOpenThreadResponseDto>(
      `/interview-scheduling/employer/${employerId}/user-profile/${userProfileId}/open-thread-exists`,
    )
      .get()
      .json();

    if (error.value) {
      throw error.value;
    }
    const { exists } = data.value ?? { exists: false };
    this.interviewSchedulingStore.currentJobApplicantHasOngoingThread = exists;
  }

  get hasOngoingInterviewThread(): boolean {
    return this.interviewSchedulingStore.currentJobApplicantHasOngoingThread;
  }

  get validationErrors(): { contacts: string | undefined; customMeetingLink: string | undefined } {
    return this.interviewSchedulingStore.validationErrors;
  }

  get hasValidationErrors(): boolean {
    return this.interviewSchedulingStore.hasValidationErrors;
  }

  resetValidationErrors(): void {
    this.interviewSchedulingStore.resetValidationErrors();
  }

  validateCustomMeetingLink(options: CustomMeetingLinkValidationOptions): void {
    const { needsUpgrade, isVideoInterview, customMeetingLink } = options;
    this.interviewSchedulingStore.validationErrors.customMeetingLink = undefined;

    if (!isVideoInterview) {
      return;
    }

    const emptyCustomLink = !customMeetingLink || customMeetingLink.trim() === '';

    if (needsUpgrade && emptyCustomLink) {
      this.interviewSchedulingStore.validationErrors.customMeetingLink =
        'Custom meeting link is required for video interviews. Add a link or reconnect your calendar.';
      return;
    }

    if (!emptyCustomLink) {
      try {
        new URL(customMeetingLink.trim());
      } catch {
        this.interviewSchedulingStore.validationErrors.customMeetingLink =
          'Custom meeting link must be a valid URL.';
        return;
      }
    }
    return;
  }

  validateContacts(contacts: unknown[]): boolean {
    const hasContacts = (contacts?.length ?? 0) > 0;
    this.interviewSchedulingStore.validationErrors.contacts = hasContacts
      ? undefined
      : 'Please add at least one team member to the interview';
    return hasContacts;
  }

  validateInterviewSettings(settings: ProjectInterviewSettings | null): boolean {
    this.interviewSchedulingStore.resetValidationErrors();

    if (!settings?.enabled) {
      return true;
    }

    this.validateContacts(settings.contacts ?? []);

    this.validateCustomMeetingLink({
      needsUpgrade: this.calendarService.currentUserNeedsUpgrade,
      isVideoInterview: settings.type === InterviewType.video,
      customMeetingLink: settings.customMeetingLink,
    });

    return !this.interviewSchedulingStore.hasValidationErrors;
  }

  /**
   * ONE-3961: Upsert interview scheduling settings for a project
   * This calls the backend endpoint: PUT /interview-scheduling/project/:projectId/settings
   */
  async upsertInterviewSettings(
    projectId: number,
    settings: ProjectInterviewSettings,
  ): Promise<unknown> {
    const employerId = this.meService.employer?.id;
    if (!employerId) {
      throw new Error('Employer ID is required');
    }

    // Convert InterviewSettings to backend DTO format
    const payload: ProjectInterviewSettings = {
      enabled: settings.enabled,
      scoreThreshold: settings.scoreThreshold,
      contacts: settings.contacts,
      duration: settings.duration,
      type: settings.type,
      venueDetails: settings.venueDetails || '',
      additionalDetails: settings.additionalDetails || '',
      schedulingPreferences: settings.schedulingPreferences || '',
      requireFinalConfirmation: settings.requireFinalConfirmation ?? false,
      recruiterEmail: settings.recruiterEmail,
      recruiterName: settings.recruiterName,
      recruiterUserProfileId: settings.recruiterUserProfileId,
      customMeetingLink: settings.customMeetingLink,
    };

    const { error, data } = await useAtsProjectsApi(
      `/interview-scheduling/project/${projectId}/settings`,
    )
      .put(payload)
      .json();

    if (error.value) {
      throw error.value;
    }

    return data.value;
  }

  /**
   * ONE-3961: Fetch existing interview scheduling settings for a project
   */
  async fetchInterviewSettings(projectId: number): Promise<ProjectInterviewSettings | null> {
    const employerId = this.meService.employer?.id;
    if (!employerId) {
      throw new Error('Employer ID is required');
    }

    const { error, data, statusCode } = await useAtsProjectsApi<unknown>(
      `/interview-scheduling/project/${projectId}/settings`,
    )
      .get()
      .json();

    if (statusCode.value === 404) {
      return null;
    }

    if (error.value) {
      throw error.value;
    }

    // Convert backend format to InterviewSettings
    if (data.value) {
      return {
        enabled: data.value.enabled,
        scoreThreshold: data.value.scoreThreshold,
        contacts: data.value.contacts,
        duration: data.value.duration,
        type: data.value.type,
        venueDetails: data.value.venueDetails || '',
        additionalDetails: data.value.additionalDetails || '',
        schedulingPreferences: data.value.schedulingPreferences || '',
        requireFinalConfirmation: data.value.requireFinalConfirmation ?? false,
        recruiterEmail: data.value.recruiterEmail,
        recruiterName: data.value.recruiterName,
        recruiterUserProfileId: data.value.recruiterUserProfileId,
        customMeetingLink: data.value.customMeetingLink,
      };
    }

    return null;
  }
}
